import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraSectionHeader, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMemo, useState } from 'react';
import { useGrowthThreadEnabled } from '../../../GrowthHub/growthFlags';
import { useIntelClaimsForLinks } from '../../../Registries/hearsay/hooks';
import { CompetitiveFieldSection } from '../../CaptureCompanyLinksPanel';
import { CustomerThemeEvidence } from '../../CustomerThemeEvidence';
import { useInvalidateHearsay } from '../hearsay/pursuitHearsay';
import { usePursuitRecord } from '../PursuitRecordContext';
import { PursuitSectionFrame } from '../PursuitSectionFrame';
import { CompetitorHearsayDrawer } from './CompetitorHearsayDrawer';
/** Claims ABOUT one company (REG resolves company merge chains on the read). */ function claimsAbout(claims, companyId) {
    return claims.filter((c)=>c.links.some((l)=>l.linkRole === 'ABOUT' && l.targetKind === 'COMPANY' && l.targetId === companyId));
}
/**
 * GRW-20 item 9: "Hearsay · n" per competitor whose company has claims the
 * viewer may see, from any context; one batched read over the competitors
 * (≤ 6, GetPursuitHeader's cap). Nothing at n = 0. Behind growth_thread_v1.
 */ export function CompetitorHearsay({ competitors }) {
    const threadOn = useGrowthThreadEnabled();
    const invalidate = useInvalidateHearsay();
    const links = useMemo(()=>competitors.filter((c)=>c.companyProfileId).map((c)=>({
                linkRole: 'ABOUT',
                targetKind: 'COMPANY',
                targetId: c.companyProfileId
            })), [
        competitors
    ]);
    const claims = useIntelClaimsForLinks(links, threadOn);
    const [openId, setOpenId] = useState('');
    if (!threadOn) return null;
    const rows = competitors.map((c)=>({
            c,
            about: claimsAbout(claims.data?.claims ?? [], c.companyProfileId)
        })).filter((r)=>r.about.length > 0);
    if (rows.length === 0) return null;
    const open = rows.find((r)=>r.c.companyProfileId === openId);
    return /*#__PURE__*/ _jsxs("section", {
        className: "space-y-2",
        children: [
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                variant: "group",
                title: "Hearsay about the competitors"
            }),
            rows.map(({ c, about })=>/*#__PURE__*/ _jsxs("div", {
                    className: "flex items-center gap-2",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraText, {
                            size: "body-ui",
                            children: c.name
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            variant: "link",
                            size: "inline",
                            "data-testid": Testids.pursuitCompetitors.hearsayBadge,
                            "data-company-id": c.companyProfileId,
                            onClick: ()=>setOpenId(c.companyProfileId),
                            children: /*#__PURE__*/ _jsxs(SpecraBadge, {
                                variant: "warning",
                                children: [
                                    "Hearsay · ",
                                    about.length
                                ]
                            })
                        })
                    ]
                }, c.participantId)),
            open && /*#__PURE__*/ _jsx(CompetitorHearsayDrawer, {
                companyName: open.c.name,
                claims: open.about,
                open: true,
                onOpenChange: (o)=>!o && setOpenId(''),
                onChanged: invalidate
            })
        ]
    });
}
/**
 * Competitors: the evidence-backed competitive field without the legacy
 * per-participant black-hat inputs (superseded by the Black hat section), plus
 * the customer-theme evidence released to competition analysis, and (GRW-20)
 * the hearsay known about each competitor.
 */ export function CompetitorsSection() {
    const { captureProjectId, correlationId, header } = usePursuitRecord();
    // GRW-30: sections always sit in the record shell, so the gate is the header capabilities.
    const { canEdit } = usePursuitRecord().capabilities;
    return /*#__PURE__*/ _jsxs(PursuitSectionFrame, {
        title: "Competitors",
        children: [
            /*#__PURE__*/ _jsx(CustomerThemeEvidence, {
                objectId: captureProjectId,
                receiver: "competition",
                readOnly: !canEdit
            }),
            /*#__PURE__*/ _jsx(CompetitorHearsay, {
                competitors: header?.competitors ?? []
            }),
            /*#__PURE__*/ _jsx(CompetitiveFieldSection, {
                readOnly: !canEdit,
                captureProjectId: captureProjectId,
                correlationId: correlationId,
                legacyInputs: false
            })
        ]
    });
}
